import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import TextField from "../../components/carbon/Form/TextField";
import SelectField from "../../components/carbon/Form/SelectField";
import PhoneField from "../../components/carbon/Form/PhoneField";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import { usePublicCountries } from "../../hooks/useCountries";
import { useUser, useNotification } from "../../context";
import { registrationCompletionValidationSchema } from "../../utils";
import { useUpdateNewSupplier } from "../../hooks/useSuppliers";
import "./registrationCompletion.scss";

type RegistrationCompletionFormValues = {
  companyName: string;
  commercialName: string;
  taxId: string;
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
};

/**
 * The invited supplier completes their pending registration (`PUT /user/pending-registration` via
 * `useUpdateNewSupplier` — principal-scoped on the backend), rebuilt on Carbon + `AuthLayout` and the
 * shared `Controller`-bound Form primitives. Three sections (company / user / password); a
 * `ConfirmDialog` gates the submit exactly as the pre-Carbon confirm `Modal` did.
 */
const RegistrationCompletion = () => {
  const { t } = useTranslation();
  const { user, getUser } = useUser();
  const { openNotification } = useNotification();
  const [open, setOpen] = useState(false);

  const { data: countries, isLoading: isLoadingCountries } =
    usePublicCountries();

  const { mutate: updateSupplier, isPending: isUpdatingSupplier } =
    useUpdateNewSupplier(async () => {
      openNotification(t("suppliers.updateSuccess"));
      await getUser();
    });

  const { control, handleSubmit } = useForm<RegistrationCompletionFormValues>({
    resolver: yupResolver(registrationCompletionValidationSchema),
    defaultValues: {
      companyName: user?.company?.name || "",
      commercialName: user?.company?.commercialName || "",
      taxId: user?.company?.taxId || "",
      country: user?.company?.country?.id || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (data: RegistrationCompletionFormValues) => {
    if (!user) return;
    updateSupplier({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      companyName: data.companyName,
      companyCommercialName: data.commercialName,
      countryId: data.country,
      companyEmail: data.email,
      companyPhone: data.phone,
      taxId: data.taxId,
      password: data.password,
      localization: user.localization,
    });
    setOpen(false);
  };

  if (isLoadingCountries || isUpdatingSupplier) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  const countryOptions = (countries || []).map((c) => ({
    value: c.id,
    label: c.name,
  }));

  return (
    <AuthLayout width="wide">
      <h1 className="registration-completion__title">
        {t("registrationCompletion.title")}
      </h1>
      <p className="registration-completion__description">
        {t("registrationCompletion.description")}
      </p>

      <form className="registration-completion__form" autoComplete="off">
        <fieldset className="registration-completion__section">
          <legend className="registration-completion__legend">
            {t("register.form.companyData")}
          </legend>
          <TextField
            control={control}
            name="companyName"
            label={t("register.form.companyName")}
          />
          <TextField
            control={control}
            name="commercialName"
            label={t("register.form.commercialName")}
            optional
          />
          <TextField
            control={control}
            name="taxId"
            label={t("register.form.tax")}
          />
          <SelectField
            control={control}
            name="country"
            label={t("profile.country")}
            placeholder={t("validations.country")}
            options={countryOptions}
          />
        </fieldset>

        <fieldset className="registration-completion__section">
          <legend className="registration-completion__legend">
            {t("register.form.userData")}
          </legend>
          <TextField
            control={control}
            name="firstName"
            label={t("register.form.firstName")}
          />
          <TextField
            control={control}
            name="lastName"
            label={t("register.form.lastName")}
          />
          <TextField
            control={control}
            name="email"
            type="email"
            label={t("register.form.email")}
          />
          <PhoneField
            control={control}
            name="phone"
            label={t("register.form.phone")}
            countryCodeLabel={t("register.form.phone")}
          />
        </fieldset>

        <fieldset className="registration-completion__section">
          <legend className="registration-completion__legend">
            {t("profile.changePassword")}
          </legend>
          <TextField
            control={control}
            name="password"
            type="password"
            label={t("register.form.password")}
          />
          <TextField
            control={control}
            name="confirmPassword"
            type="password"
            label={t("register.form.confirmPassword")}
          />
        </fieldset>

        <div className="registration-completion__actions">
          <Button type="button" onClick={() => setOpen(true)}>
            {t("registrationCompletion.submit")}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={open}
        title={t("registrationCompletion.confirmTitle")}
        body={t("registrationCompletion.confirmMessage")}
        confirmLabel={t("cta.confirm")}
        onConfirm={handleSubmit(onSubmit, () => setOpen(false))}
        onCancel={() => setOpen(false)}
      />
    </AuthLayout>
  );
};

export default RegistrationCompletion;
